import { PRNG } from './prng.ts';
import { sound } from './audio.ts';
import { FXSystem } from './particles.ts';
import type { ChallengeData } from './challenge.ts';
import { calculateAuraGain } from './progression.ts';

export type GameState = 'IDLE' | 'PLAYING' | 'GAMEOVER';
export type HitQuality = 'PERFECT' | 'GREAT' | 'GOOD' | 'MISS';

export interface GameResult {
  score: number;
  maxCombo: number;
  perfectHits: number;
  greatHits: number;
  goodHits: number;
  okHits: number; // Backwards-compatible alias for goodHits
  missHits: number;
  auraDelta: number;
  seed: string;
  opponent: ChallengeData | null;
  outauraWon: boolean;
  isDaily?: boolean;
}

export class LockInGame {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private fx: FXSystem;

  // Game Loop & Timing
  public state: GameState = 'IDLE';
  private lastTime: number = 0;
  private timeRemaining: number = 20.0;
  public readonly TOTAL_TIME: number = 20.0;
  private lastCountdownSec: number = -1;

  public getTimeRemaining(): number {
    return this.timeRemaining;
  }

  // PRNG Seed
  public seed: string = '';
  private prng!: PRNG;

  // Opponent challenge / Daily mode
  public opponent: ChallengeData | null = null;
  public isDaily: boolean = false;

  // Scoring & Stats
  public score: number = 0;
  public combo: number = 0;
  public maxCombo: number = 0;
  public perfectHits: number = 0;
  public greatHits: number = 0;
  public goodHits: number = 0;
  public missHits: number = 0;

  // Track & Marker Mechanics
  private trackWidth: number = 0;
  private trackHeight: number = 0;
  private trackX: number = 0;
  private trackY: number = 0;

  private targetNormalized: number = 0.5; // 0.0 to 1.0 along track
  private targetWidthNormalized: number = 0.26; // width fraction

  private markerNormalized: number = 0.0;
  private markerDirection: number = 1;
  private baseSpeed: number = 1.3;
  private speedMultiplier: number = 1.0;

  // Callbacks
  private onGameOverCallback?: (result: GameResult) => void;

  constructor(canvas: HTMLCanvasElement, fx: FXSystem) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not get 2D context');
    this.ctx = context;
    this.fx = fx;

    this.resize();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => this.resize());
    }
  }

  public setGameOverCallback(cb: (result: GameResult) => void): void {
    this.onGameOverCallback = cb;
  }

  public resize(): void {
    const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    const rect = this.canvas.parentElement?.getBoundingClientRect() || this.canvas.getBoundingClientRect();

    const w = rect.width;
    const h = rect.height;

    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;

    this.ctx.resetTransform();
    this.ctx.scale(dpr, dpr);

    // Layout geometry
    this.trackWidth = Math.min(w * 0.88, 400);
    this.trackHeight = 48;
    this.trackX = (w - this.trackWidth) / 2;
    this.trackY = h * 0.38;
  }

  public initGame(
    seed: string = PRNG.generateSeedCode(),
    opponent: ChallengeData | null = null,
    isDaily: boolean = false
  ): void {
    this.seed = seed;
    this.opponent = opponent;
    this.isDaily = isDaily;
    this.prng = new PRNG(this.seed);

    this.score = 0;
    this.combo = 0;
    this.maxCombo = 0;
    this.perfectHits = 0;
    this.greatHits = 0;
    this.goodHits = 0;
    this.missHits = 0;
    this.timeRemaining = this.TOTAL_TIME;
    this.lastCountdownSec = -1;

    this.markerNormalized = 0.0;
    this.markerDirection = 1;
    this.baseSpeed = 1.3;
    this.speedMultiplier = 1.0;

    this.targetNormalized = this.prng.nextRange(0.25, 0.75);
    this.targetWidthNormalized = 0.26;

    this.state = 'IDLE';
    this.fx.clear();
  }

  public start(): void {
    sound.unlock();
    this.state = 'PLAYING';
    this.lastTime = performance.now();
  }

  public getMultiplier(): number {
    if (this.combo >= 20) return 5;
    if (this.combo >= 15) return 4;
    if (this.combo >= 10) return 3;
    if (this.combo >= 5) return 2;
    return 1;
  }

  public handleTap(): void {
    if (this.state === 'IDLE') {
      this.start();
      return;
    }
    if (this.state !== 'PLAYING') return;

    sound.unlock();

    const distance = Math.abs(this.markerNormalized - this.targetNormalized);
    const halfTarget = this.targetWidthNormalized / 2;

    let quality: HitQuality;
    let scoreGained = 0;
    const markerScreenX = this.trackX + this.markerNormalized * this.trackWidth;
    const markerScreenY = this.trackY + this.trackHeight / 2;

    const multiplier = this.getMultiplier();

    const EPS = 1e-5;
    if (distance <= halfTarget * 0.30 + EPS) {
      // PERFECT HIT
      quality = 'PERFECT';
      this.perfectHits++;
      this.combo++;
      scoreGained = 500 * multiplier;
      this.score += scoreGained;
      sound.playPerfect(this.combo);
    } else if (distance <= halfTarget * 0.70 + EPS) {
      // GREAT HIT
      quality = 'GREAT';
      this.greatHits++;
      this.combo++;
      scoreGained = 250 * multiplier;
      this.score += scoreGained;
      sound.playGreat();
    } else if (distance <= halfTarget * 1.05 + EPS) {
      // GOOD HIT
      quality = 'GOOD';
      this.goodHits++;
      this.combo++;
      scoreGained = 100 * multiplier;
      this.score += scoreGained;
      sound.playGood();
    } else {
      // MISS
      quality = 'MISS';
      this.missHits++;
      this.combo = 0; // Combo breaks!
      sound.playMiss();
    }

    if (this.combo > this.maxCombo) {
      this.maxCombo = this.combo;
    }

    // Trigger visual FX for hit
    this.fx.triggerHit(markerScreenX, markerScreenY, quality, this.combo, scoreGained);

    // Milestone triggers at 5x, 10x, 15x, 20x
    if (this.combo === 5 || this.combo === 10 || this.combo === 15 || this.combo === 20) {
      sound.playComboMilestone(this.combo);
      this.fx.triggerMilestone(markerScreenX, markerScreenY, this.combo);
    }

    // Deterministically reposition target
    this.advanceTarget(quality !== 'MISS');
  }

  private advanceTarget(hitSuccess: boolean): void {
    if (hitSuccess) {
      // Target jump location from PRNG
      let nextPos = this.prng.nextRange(0.2, 0.8);
      if (Math.abs(nextPos - this.targetNormalized) < 0.22) {
        nextPos = (nextPos + 0.45) % 0.8 + 0.15;
      }
      this.targetNormalized = nextPos;

      // Progressive difficulty ramp calculation
      this.applyDifficultyRamp();
    }
  }

  private applyDifficultyRamp(): void {
    const elapsed = this.TOTAL_TIME - this.timeRemaining;

    // 0-5s: Easy
    // 5-10s: Moderate
    // 10-15s: Fast
    // 15-20s: Chaotic
    let baseSweep = 1.3;
    let targetWidth = 0.26;

    if (elapsed >= 15) {
      // Final 5 seconds (15-20s): Chaotic
      baseSweep = 2.6;
      targetWidth = 0.14;
    } else if (elapsed >= 10) {
      // 10-15s: Fast
      baseSweep = 2.1;
      targetWidth = 0.18;
    } else if (elapsed >= 5) {
      // 5-10s: Moderate
      baseSweep = 1.7;
      targetWidth = 0.22;
    }

    // Combo streak micro-boost (up to +0.3)
    const comboBoost = Math.min(this.combo * 0.02, 0.35);
    this.speedMultiplier = (baseSweep / 1.3) + comboBoost;
    this.targetWidthNormalized = Math.max(0.12, targetWidth);
  }

  public update(now: number): void {
    if (this.lastTime === 0) this.lastTime = now;
    const dt = Math.min((now - this.lastTime) / 1000, 0.1);
    this.lastTime = now;

    this.fx.update(dt);

    if (this.state === 'PLAYING') {
      this.timeRemaining -= dt;

      // Final 5 seconds countdown emphasis
      if (this.timeRemaining <= 5.0 && this.timeRemaining > 0) {
        const sec = Math.ceil(this.timeRemaining);
        if (sec !== this.lastCountdownSec && sec >= 1 && sec <= 5) {
          this.lastCountdownSec = sec;
          sound.playCountdown(sec);
          this.fx.shake(2);
        }
      }

      if (this.timeRemaining <= 0) {
        this.timeRemaining = 0;
        this.finishGame();
        return;
      }

      // Continuous difficulty progression
      this.applyDifficultyRamp();

      // Marker oscillation
      const sweepRate = this.baseSpeed * this.speedMultiplier;
      this.markerNormalized += this.markerDirection * sweepRate * dt;

      if (this.markerNormalized >= 1.0) {
        this.markerNormalized = 1.0;
        this.markerDirection = -1;
      } else if (this.markerNormalized <= 0.0) {
        this.markerNormalized = 0.0;
        this.markerDirection = 1;
      }
    }
  }

  private finishGame(): void {
    this.state = 'GAMEOVER';

    let outauraWon = true;
    let isWin: boolean | undefined = undefined;

    if (this.opponent) {
      outauraWon = this.score > this.opponent.score;
      isWin = outauraWon;
      if (outauraWon) {
        sound.playVictory();
      } else {
        sound.playDefeat();
      }
    } else {
      if (this.score >= 5000) {
        sound.playVictory();
      } else {
        sound.playGood();
      }
    }

    let auraGain = calculateAuraGain(this.score, this.perfectHits, this.greatHits, this.maxCombo, isWin);

    // Daily challenge completion bonus (+100 Aura)
    if (this.isDaily) {
      auraGain += 100;
    }

    const result: GameResult = {
      score: this.score,
      maxCombo: this.maxCombo,
      perfectHits: this.perfectHits,
      greatHits: this.greatHits,
      goodHits: this.goodHits,
      okHits: this.goodHits, // For backwards compatibility
      missHits: this.missHits,
      auraDelta: auraGain,
      seed: this.seed,
      opponent: this.opponent,
      outauraWon,
      isDaily: this.isDaily,
    };

    if (this.onGameOverCallback) {
      this.onGameOverCallback(result);
    }
  }

  public render(): void {
    const w = this.canvas.parentElement?.clientWidth || this.canvas.width;
    const h = this.canvas.parentElement?.clientHeight || this.canvas.height;
    const ctx = this.ctx;

    ctx.save();

    // Apply screen shake
    ctx.translate(this.fx.shakeX, this.fx.shakeY);

    // Clear background
    ctx.clearRect(0, 0, w, h);

    // Dynamic background aura effects reflecting combo tier
    if (this.combo >= 5) {
      const grad = ctx.createRadialGradient(w / 2, this.trackY + this.trackHeight / 2, 20, w / 2, this.trackY, w * 0.85);

      if (this.combo >= 20) {
        // Mythic Aura Corona (Gold + Electric Cyan)
        grad.addColorStop(0, 'rgba(251, 191, 36, 0.35)');
        grad.addColorStop(0.4, 'rgba(56, 189, 248, 0.2)');
        grad.addColorStop(1, 'rgba(7, 8, 13, 0)');
      } else if (this.combo >= 15) {
        // Aura Demon (Purple + Rose)
        grad.addColorStop(0, 'rgba(192, 132, 252, 0.3)');
        grad.addColorStop(0.5, 'rgba(244, 63, 94, 0.15)');
        grad.addColorStop(1, 'rgba(7, 8, 13, 0)');
      } else if (this.combo >= 10) {
        // Overdrive (Gold)
        grad.addColorStop(0, 'rgba(251, 191, 36, 0.25)');
        grad.addColorStop(0.5, 'rgba(168, 85, 247, 0.12)');
        grad.addColorStop(1, 'rgba(7, 8, 13, 0)');
      } else {
        // Locked In (Cyan + Violet)
        grad.addColorStop(0, 'rgba(56, 189, 248, 0.2)');
        grad.addColorStop(0.5, 'rgba(168, 85, 247, 0.1)');
        grad.addColorStop(1, 'rgba(7, 8, 13, 0)');
      }

      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
    }

    // Final 5 seconds subtle countdown tension pulse
    if (this.state === 'PLAYING' && this.timeRemaining <= 5.0 && this.timeRemaining > 0) {
      const sec = Math.ceil(this.timeRemaining);
      const frac = this.timeRemaining % 1; // 1 down to 0 inside each second
      const pulseAlpha = Math.sin(frac * Math.PI) * 0.16;

      ctx.save();
      ctx.fillStyle = `rgba(244, 63, 94, ${pulseAlpha.toFixed(3)})`;
      ctx.fillRect(0, 0, w, h);

      // Render large translucent countdown digit in the background behind the track
      ctx.font = '900 120px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = `rgba(251, 191, 36, ${(pulseAlpha * 1.8).toFixed(3)})`;
      ctx.fillText(String(sec), w / 2, this.trackY + this.trackHeight / 2);
      ctx.restore();
    }

    // Render Track Base
    this.renderTrack(ctx);

    // Render Particles, Flashes, & Floats
    this.fx.render(ctx, w, h);

    ctx.restore();
  }

  private renderTrack(ctx: CanvasRenderingContext2D): void {
    const tx = this.trackX;
    const ty = this.trackY;
    const tw = this.trackWidth;
    const th = this.trackHeight;
    const radius = th / 2;

    const isFinalSeconds = this.state === 'PLAYING' && this.timeRemaining <= 5.0;

    // Track outer border glow
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(tx, ty, tw, th, radius);
    ctx.fillStyle = '#101321';
    ctx.fill();

    let strokeColor = '#252a42';
    if (isFinalSeconds) {
      strokeColor = '#f43f5e';
    } else if (this.combo >= 20) {
      strokeColor = '#fbbf24';
    } else if (this.combo >= 15) {
      strokeColor = '#c084fc';
    } else if (this.combo >= 10) {
      strokeColor = '#fbbf24';
    } else if (this.combo >= 5) {
      strokeColor = '#38bdf8';
    }

    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = this.combo >= 5 || isFinalSeconds ? 3 : 2;
    if (this.combo >= 5 || isFinalSeconds) {
      ctx.shadowColor = strokeColor;
      ctx.shadowBlur = 14;
    }
    ctx.stroke();
    ctx.restore();

    // Target Zone
    const targetW = tw * this.targetWidthNormalized;
    const targetX = tx + this.targetNormalized * tw - targetW / 2;

    // Target Zone Gradient
    ctx.save();
    const targetGrad = ctx.createLinearGradient(targetX, ty, targetX + targetW, ty);
    targetGrad.addColorStop(0, 'rgba(56, 189, 248, 0.15)');
    targetGrad.addColorStop(0.5, 'rgba(168, 85, 247, 0.45)');
    targetGrad.addColorStop(1, 'rgba(56, 189, 248, 0.15)');

    ctx.beginPath();
    ctx.roundRect(Math.max(tx, targetX), ty + 3, Math.min(targetW, tw - 6), th - 6, 8);
    ctx.fillStyle = targetGrad;
    ctx.fill();

    // Target Sweetspot (Center Line)
    const centerX = tx + this.targetNormalized * tw;
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 3;
    ctx.shadowColor = '#38bdf8';
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.moveTo(centerX, ty + 4);
    ctx.lineTo(centerX, ty + th - 4);
    ctx.stroke();
    ctx.restore();

    // Sweeper Marker
    const markerX = tx + this.markerNormalized * tw;
    let markerColor = '#a855f7';
    if (this.combo >= 20) {
      markerColor = '#fef08a';
    } else if (this.combo >= 10) {
      markerColor = '#fbbf24';
    } else if (this.combo >= 5) {
      markerColor = '#38bdf8';
    }

    ctx.save();
    ctx.shadowColor = markerColor;
    ctx.shadowBlur = 16;
    ctx.fillStyle = markerColor;

    // Marker pill
    ctx.beginPath();
    ctx.roundRect(markerX - 5, ty - 6, 10, th + 12, 5);
    ctx.fill();

    // Inner highlight
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(markerX - 1.5, ty - 2, 3, th + 4, 2);
    ctx.fill();
    ctx.restore();
  }
}
